import { useEffect, useRef } from "react";
import L from "leaflet";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const STATUS_COLOR = { created: "#64748B", dispatched: "#EAB308", in_transit: "#2563EB", delivered: "#16A34A" };

const pin = (bg, label) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${bg};color:#fff;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">${label}</div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
  });

export default function ShipmentMap({ shipments = [], height = "100%", focus = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // init once, guarded against double-init (React 19 / HMR)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current || el._leaflet_id) return;
    const map = L.map(el, { scrollWheelZoom: true }).setView([51.5074, -0.1278], 8);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // redraw layers on data change
  useEffect(() => {
    const map = mapRef.current;
    const grp = layerRef.current;
    if (!map || !grp) return;
    grp.clearLayers();
    const list = focus ? [focus] : shipments;
    const bounds = [];
    list.forEach((s) => {
      const color = STATUS_COLOR[s.status] || "#64748B";
      const o = [s.origin.lat, s.origin.lng];
      const d = [s.destination.lat, s.destination.lng];
      L.marker(o, { icon: pin("#0F172A", "O") }).bindPopup(`${s.origin.name} · Origin`).addTo(grp);
      L.marker(d, { icon: pin("#16A34A", "D") }).bindPopup(`${s.destination.name} · Destination`).addTo(grp);
      const line = [o];
      if (s.last_location) line.push([s.last_location.lat, s.last_location.lng]);
      line.push(d);
      L.polyline(line, { color, weight: 3, dashArray: s.status === "delivered" ? null : "6 8", opacity: 0.7 }).addTo(grp);
      if (s.last_location) {
        L.circleMarker([s.last_location.lat, s.last_location.lng], { color: "#fff", weight: 2, fillColor: color, fillOpacity: 1, radius: 8 })
          .bindPopup(`<b>${s.code}</b><br/>Last known: ${s.last_location.lat.toFixed(3)}, ${s.last_location.lng.toFixed(3)}<br/>${new Date(s.last_location.ts).toLocaleString()}`)
          .addTo(grp);
      }
      bounds.push(o, d);
    });
    if (bounds.length) {
      try { map.fitBounds(bounds, { padding: [40, 40], maxZoom: focus ? 8 : 7 }); } catch { /* noop */ }
    }
  }, [shipments, focus]);

  return <div ref={containerRef} style={{ height, width: "100%" }} data-testid="shipment-map" />;
}
